import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { GridConfig, GridHyperlinkClickEvent } from '@inavista/components/grid';
import { environment } from 'projects/service/src/environments/environment';
import { OrderTypes } from '../../models/app.model';
import { CommonService } from '../../services/common.service';

// "Adjustment Orders — the following adjustment order(s) were created", shown after Create Adjustment Order (Inventory
// Exceptions, Pending Approvals, Cycle Counts, the shared syncfusion-grid). The hosts still pass the rows
// ShipmentOrder/CreateAdjustmentOrder returned; the grid reads the same rows for those order numbers from
// api/grid/created-adjustment-orders (only orders the signed-in user created).
@Component({
    selector: 'app-ms-dynamic-modal',
    templateUrl: './ms-dynamic-modal.component.html',
    styleUrls: ['./ms-dynamic-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MsDynamicModalComponent implements OnChanges {
  isExpanded = false;

  @Input() pageId: string;
  @Input() modalTitle = 'List';
  @Input() modalTitleLabel: string = null;
  @Input() data: any = {};

  @Output() onClose = new EventEmitter();
  gridConfig: GridConfig | null = null;
  linkedPopupData: { id: any; type: any; };

  constructor(private commonService: CommonService) {}

  ngOnChanges() {
    const ids = (Array.isArray(this.data) ? this.data : []).map(row => row?.adjustmentOrderNumber).filter(Boolean);
    const canExport = !!this.commonService.roleClaims?.['AllGrid_Toolbar_Excel_Export']?.visible;
    this.gridConfig = ids.length ? { ...buildCreatedAdjustmentOrdersGridConfig(ids), enableExport: canExport } : null;
  }

  close() {
    this.onClose.emit();
  }

  // Adjustment Order ID: the adjustment order popup, as before.
  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.linkedPopupData = { id: event.row['adjustmentOrderNumber'], type: OrderTypes.adjustmentorder };
  }
}

// Same columns and labels as the Syncfusion grid it replaces (Sr. No. is the procedure's row number).
function buildCreatedAdjustmentOrdersGridConfig(ids: string[]): GridConfig {
  const order = ['id', 'adjustmentOrderNumber', 'technicianName', 'createdDate', 'createdBy'];
  return {
    apiBaseUrl: `${environment.apiUrl}grid/created-adjustment-orders`,
    clientSide: true,
    queryParams: { ids: ids.join(',') },
    keyColumn: 'adjustmentOrderNumber',
    defaultColumnOrder: order,
    hiddenByDefaultColumns: ['company', 'status', 'reasonForAdjustment', 'warehouse', 'technicianId', 'createdByName',
      'lastUpdatedDate', 'lastUpdatedBy', 'lastUpdatedByName'],
    columnLabels: {
      id: 'Sr. No.', adjustmentOrderNumber: 'Adjustment Order ID', technicianName: 'Technician', createdDate: 'Created Date',
      createdBy: 'Created By',
    },
    columnDateFormats: { createdDate: 'd MMM, yyyy' },
    // RIMS grid look: headers left; text and dates left; codes and IDs centred.
    columnAlign: Object.fromEntries(order.map(c => [c, {
      header: { horizontal: 'left' as const },
      ...(['id', 'adjustmentOrderNumber', 'createdBy'].includes(c) ? { horizontal: 'center' as const } : {}),
    }])),
    columnWidths: { id: 90, adjustmentOrderNumber: 160, technicianName: 200, createdDate: 140, createdBy: 130 },
    stretchColumns: true,
    hyperlinkColumns: ['adjustmentOrderNumber'],
    excelFilterColumns: ['technicianName'],
    defaultSort: { column: 'id', direction: 'asc' },
    enableContextMenuFilter: true,
    enableResetLayout: true,
    wrapHeaders: true,
    rowHeight: 26,
    loadingIndicator: 'banner',
    emptyStateMessage: 'No adjustment order was created.',
    persistenceKey: 'rims.service.created-adjustment-orders',
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
    persistenceVersion: 1,
  };
}
